<template>
  <v-container id="tax-agent-create-page">
    <!-- Back button -->
    <v-row no-gutters class="mb-2">
      <v-btn
        class="back-button"
        text
        exact
        @click="$router.go(-1)">
        <v-icon left>
          mdi-chevron-left
        </v-icon>
        กลับ
      </v-btn>
    </v-row>

    <!-- Page title -->
    <v-row no-gutters class="mb-1">
      <v-col cols="12">
        <div class="page-title">
          แต่งตั้งให้ Sellsuki เป็นตัวแทนหักและนำส่งภาษี ณ ที่จ่าย
        </div>
        <div class="page-subtitle">
          {{ pageSubtitle }}
        </div>
      </v-col>
    </v-row>

    <!-- Card 1: ข้อมูลร้านค้า หรือบริษัท -->
    <v-card class="user-info mb-4">
      <!-- Edit error banner -->
      <div v-if="isEdit && hasRejectedCompany" class="edit-error-banner mb-6">
        <v-icon color="#DC3F5A" size="20">mdi-alert-circle</v-icon>
        <span>กรุณาแก้ไขข้อมูล</span>
      </div>

      <!-- Card header -->
      <v-row no-gutters class="mb-4" align="center">
        <v-col>
          <span class="_fs-20">{{ SHOP_OR_COMPANY_LABEL }}</span>
        </v-col>
        <v-col cols="auto">
          <span
            v-if="isEdit && companyStatus === TaxAgentStatus.Approved"
            class="status-badge status-badge--approved"
          >
            Approved
          </span>
          <v-btn
            v-else
            text
            small
            color="primary"
            :to="{ name: 'ProfileEdit' }"
          >
            <v-icon
              left
              small>
              mdi-pencil
            </v-icon>
            แก้ไขข้อมูล
          </v-btn>
        </v-col>
      </v-row>

      <!-- OA info -->
      <v-row>
        <v-col cols="6" md="10" class="d-flex flex-column justify-center">
          <div>
            <span class="font-weight-bold">Display Name:</span>
            <br class="d-md-none" />
            <span class="primary--text">
              {{ activeLineOfficialAccount.displayName }}
            </span>
          </div>
          <div>
            <span class="font-weight-bold">Basic ID:</span>
            <br class="d-md-none" />
            <span class="primary--text">
              {{ activeBasicId }}
            </span>
          </div>
        </v-col>
      </v-row>

      <p class="mt-4 _fs-20">
        {{ SHOP_OR_COMPANY_LABEL }}
      </p>
      <div>
        ประเภทผู้เสียภาษี
        <span class="d-block d-md-inline-block primary--text font-weight-bold">
          {{ $utils.customerTypeText(customerTypes, activeLineOfficialAccount.customerTypeId) }}
        </span>
      </div>

      <div v-if="isJuristicCustomer">
        เลขที่สาขา
        <span class="d-block d-md-inline-block font-weight-bold">
          {{ activeLineOfficialAccount.branchNumber }}
          <template v-if="$utils.isHeadquarter(activeLineOfficialAccount.branchNumber)">
            (สำนักงานใหญ่)
          </template>
        </span>
      </div>

      <div v-if="isJuristicCustomer">
        {{ $utils.citizenIdText(isJuristicCustomer) }}
        <span class="d-block d-md-inline-block font-weight-bold">
          {{ activeLineOfficialAccount.citizenId }}
        </span>
      </div>

      <div>
        ชื่อร้านค้าหรือบริษัท
        <span class="d-block d-md-inline-block font-weight-bold">
          {{ activeLineOfficialAccount.companyName }}
        </span>
      </div>

      <p class="mt-4 _fs-20">
        {{ SHOP_OR_COMPANY_LABEL }}
      </p>
      <v-row no-gutters>
        <v-col cols="6">
          ชื่อ
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.firstName }}</span
          >
        </v-col>
        <v-col cols="6">
          นามสกุล
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.lastName }}
          </span>
        </v-col>
        <v-col cols="6">
          Email สำหรับส่งใบกำกับภาษี
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.email }}
          </span>
        </v-col>
        <v-col cols="6">
          Email สำหรับส่งใบเสนอราคา
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.emailQuotation }}
          </span>
        </v-col>
        <v-col cols="6">
          โทรศัพท์
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.tel }}
          </span>
        </v-col>
      </v-row>

      <p class="mt-4 _fs-20">
        ที่อยู่ของร้านค้า หรือบริษัท
      </p>
      <v-row no-gutters>
        <v-col cols="12">
          ที่อยู่
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.address }}
          </span>
        </v-col>
        <v-col cols="6">
          ถนน
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.street }}
          </span>
        </v-col>
        <v-col cols="6">
          แขวง/ตำบล
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.subdistrict }}
          </span>
        </v-col>
        <v-col cols="6">
          เขต/อำเภอ
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.district }}
          </span>
        </v-col>
        <v-col cols="6">
          จังหวัด
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.stateProvince }}
          </span>
        </v-col>
        <v-col cols="6">
          รหัสไปรษณีย์
          <span class="d-block d-md-inline-block font-weight-bold">
            {{ activeLineOfficialAccount.zipCode }}
          </span>
        </v-col>
      </v-row>
    </v-card>

    <!-- Card 2: แนบเอกสาร -->
    <v-card class="tax-attachment mb-4">
      <div class="section-header mb-4">
        แนบเอกสาร
      </div>
      <v-divider class="mb-6"></v-divider>

      <!-- Section 1: หนังสือรับรองบริษัท -->
      <div class="upload-section mb-6">
        <div class="upload-label mb-1 d-flex align-center justify-space-between">
          <span>1. หนังสือรับรองบริษัท<span class="required-mark">*</span></span>
          <span
            v-if="isEdit && documentSectionStatus.companyCertificate === TaxAgentStatus.Approved"
            class="status-badge status-badge--approved"
          >
            Approved
          </span>
        </div>
        <div class="upload-description mb-2">
          หนังสือรับรองบริษัทและต้องมีลายมือชื่อกรรมการผู้มีอำนาจ อายุไม่เกิน 6 เดือน
        </div>
        <div
          v-if="!isEdit || documentSectionStatus.companyCertificate !== TaxAgentStatus.Approved"
          class="upload-container mb-2"
        >
          <div class="upload-content-icon">
            <v-icon :color="emptyImageError.companyCertificate ? '#DC3F5A' : '#4CAF50'" size="24">
              mdi-camera
            </v-icon>
          </div>
          <div class="upload-container-content">
            <div class="upload-area">
              <div class="upload-area-left">
                <div v-if="documents.companyCertificate.length > 0" class="upload-content">
                  <span
                    :class="
                      uploadErrors.companyCertificate
                        ? 'upload-content-header-error'
                        : 'upload-content-header'
                    "
                    color="#666666"
                    >หนังสือรับรองบริษัท</span
                  >
                  <span class="primary--text _fs-16">
                    {{ documents.companyCertificate.length }}/5 ไฟล์
                  </span>
                </div>
                <span
                  v-else
                  :class="
                    uploadErrors.companyCertificate
                      ? 'upload-counter-error'
                      : 'upload-counter'
                  "
                >
                  หนังสือรับรองบริษัท (0/5)
                </span>
              </div>
              <div
                @click="
                  documents.companyCertificate.length < MAX_FILES_PER_SECTION &&
                    triggerFileInput('companyCertificateInput')
                "
              >
                <v-icon
                  :class="
                    documents.companyCertificate.length < MAX_FILES_PER_SECTION
                      ? 'upload-area-button'
                      : 'upload-area-button--disabled'
                  "
                  :color="emptyImageError.companyCertificate ? '#DC3F5A' : undefined"
                >
                  mdi-tray-arrow-up
                </v-icon>
              </div>
            </div>
            <input
              ref="companyCertificateInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              class="hidden-input"
              @change="handleFileChange($event, 'companyCertificate')"
            />
            <div
              class="mt-1 pl-6"
              :class="uploadErrors.companyCertificate ? 'upload-error' : 'upload-helper '"
            >
              กรุณาอัพโหลดหนังสือรับรองบริษัท (สูงสุด 5 ไฟล์) รองรับไฟล์ jpg, png, pdf
              และมีขนาดไฟล์ไม่เกิน 10mb
            </div>
            <div
              v-for="(item, index) in documents.companyCertificate"
              :key="index"
              class="file-item mt-1"
            >
              <v-icon size="20" color="#00BB03">mdi-file</v-icon>
              <div class="file-info">
                <span class="file-name">{{ item.name }}</span>
                <span class="file-size grey--text">{{ item.size }}</span>
              </div>
              <v-btn icon x-small @click="removeFile('companyCertificate', index)">
                <v-icon size="18" color="#757575">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mb-6"></v-divider>

      <!-- Section 2: สำเนาบัตรประจำตัวประชาชน -->
      <div class="upload-section mb-6">
        <div class="upload-label mb-1 d-flex align-center justify-space-between">
          <span>2. สำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ<span class="required-mark">*</span></span>
          <span
            v-if="isEdit && documentSectionStatus.idCardCopy === TaxAgentStatus.Approved"
            class="status-badge status-badge--approved"
          >
            Approved
          </span>
        </div>
        <div class="upload-description mb-2">
          สำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ พร้อมลงนามรับรองสำเนา
        </div>
        <div
          v-if="!isEdit || documentSectionStatus.idCardCopy !== TaxAgentStatus.Approved"
          class="upload-container mb-2"
        >
          <div class="upload-content-icon">
            <v-icon
              :color="emptyImageError.idCardCopy ? '#DC3F5A' : '#4CAF50'"
              size="24"
            >
              mdi-camera
            </v-icon>
          </div>
          <div class="upload-container-content">
            <div class="upload-area">
              <div class="upload-area-left">
                <div
                  v-if="documents.idCardCopy.length > 0"
                  class="upload-content"
                >
                  <span
                    :class="
                      uploadErrors.idCardCopy
                        ? 'upload-content-header-error'
                        : 'upload-content-header'
                    "
                    color="#666666"
                    >สำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ</span
                  >
                  <span class="primary--text _fs-16"> {{ documents.idCardCopy.length }}/5 ไฟล์ </span>
                </div>
                <span
                  v-else
                  :class="
                    uploadErrors.idCardCopy ? 'upload-counter-error' : 'upload-counter'
                  "
                >
                  สำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ (0/5)
                </span>
              </div>
              <div
                @click="
                  documents.idCardCopy.length < MAX_FILES_PER_SECTION &&
                    triggerFileInput('idCardCopyInput')
                "
              >
                <v-icon
                  :class="
                    documents.idCardCopy.length < MAX_FILES_PER_SECTION
                      ? 'upload-area-button'
                      : 'upload-area-button--disabled'
                  "
                  :color="emptyImageError.idCardCopy ? '#DC3F5A' : undefined"
                >
                  mdi-tray-arrow-up
                </v-icon>
              </div>
            </div>
            <input
              ref="idCardCopyInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              class="hidden-input"
              @change="handleFileChange($event, 'idCardCopy')"
            />
            <div
              class="mt-1 pl-6"
              :class="uploadErrors.idCardCopy ? 'upload-error' : 'upload-helper '"
            >
              กรุณาอัพโหลดสำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ (สูงสุด 5 ไฟล์) รองรับไฟล์ jpg, png, pdf
              และมีขนาดไฟล์ไม่เกิน 10mb
            </div>
            <div v-for="(item, index) in documents.idCardCopy" :key="index" class="file-item mt-1">
              <v-icon size="20" color="#00BB03">mdi-file</v-icon>
              <div class="file-info">
                <span class="file-name">{{ item.name }}</span>
                <span class="file-size grey--text">{{ item.size }}</span>
              </div>
              <v-btn icon x-small @click="removeFile('idCardCopy', index)">
                <v-icon size="18" color="#757575">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mb-6"></v-divider>

      <!-- Section 3: เอกสารการแต่งตั้ง -->
      <div class="upload-section mb-6">
        <div class="upload-label mb-1 d-flex align-center justify-space-between">
          <span>3. เอกสารการแต่งตั้ง บริษัท เซลสุกิ จำกัด<span class="required-mark">*</span></span>
          <span
            v-if="isEdit && documentSectionStatus.taxAgent === TaxAgentStatus.Approved"
            class="status-badge status-badge--approved"
          >
            Approved
          </span>
        </div>
        <div
          v-if="!isEdit || documentSectionStatus.taxAgent !== TaxAgentStatus.Approved"
          class="upload-container mb-2"
        >
          <div class="upload-content-icon">
            <v-icon
              :color="emptyImageError.taxAgent ? '#DC3F5A' : '#4CAF50'"
              size="24"
            >
              mdi-camera
            </v-icon>
          </div>
          <div class="upload-container-content">
            <div class="upload-area">
              <div class="upload-area-left">
                <div
                  v-if="documents.taxAgent.length > 0"
                  class="upload-content"
                >
                  <span
                    :class="
                      uploadErrors.taxAgent
                        ? 'upload-content-header-error'
                        : 'upload-content-header'
                    "
                    color="#666666"
                  >เอกสารการแต่งตั้ง บริษัท เซลสุกิ จำกัด</span
                  >
                  <span class="primary--text _fs-16">
                    {{ documents.taxAgent.length }}/5 ไฟล์
                  </span>
                </div>
                <span
                  v-else
                  :class="
                    uploadErrors.taxAgent ? 'upload-counter-error' : 'upload-counter'
                  "
                >
                  เอกสารการแต่งตั้ง บริษัท เซลสุกิ จำกัด (0/5)
                </span>
              </div>
              <div
                @click="
                  documents.taxAgent.length < MAX_FILES_PER_SECTION &&
                    triggerFileInput('taxAgentInput')
                "
              >
                <v-icon
                  :class="
                    documents.taxAgent.length < MAX_FILES_PER_SECTION
                      ? 'upload-area-button'
                      : 'upload-area-button--disabled'
                  "
                  :color="emptyImageError.taxAgent ? '#DC3F5A' : undefined"
                >
                  mdi-tray-arrow-up
                </v-icon>
              </div>
            </div>
            <input
              ref="taxAgentInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              class="hidden-input"
              @change="handleFileChange($event, 'taxAgent')"
            />
            <div
              class="mt-1 pl-6"
              :class="uploadErrors.taxAgent ? 'upload-error' : 'upload-helper '"
            >
              กรุณาอัพโหลดเอกสารการแต่งตั้ง บริษัท เซลสุกิ จำกัด (สูงสุด 5 ไฟล์) รองรับไฟล์ jpg, png,
              pdf และมีขนาดไฟล์ไม่เกิน 10mb
            </div>
            <div
              v-for="(item, index) in documents.taxAgent"
              :key="index"
              class="file-item mt-1">
              <v-icon size="20" color="#00BB03">mdi-file</v-icon>
              <div class="file-info">
                <span class="file-name">{{ item.name }}</span>
                <span class="file-size grey--text">{{ item.size }}</span>
              </div>
              <v-btn icon x-small @click="removeFile('taxAgent', index)">
                <v-icon size="18" color="#757575">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mb-6"></v-divider>

      <!-- Section 4: เอกสารอื่น ๆ (optional) -->
      <div class="upload-section mb-2">
        <div class="upload-label mb-1 d-flex align-center justify-space-between">
          <span>4. เอกสารอื่น ๆ</span>
          <span
            v-if="isEdit && documentSectionStatus.other === TaxAgentStatus.Approved"
            class="status-badge status-badge--approved"
          >
            Approved
          </span>
        </div>
        <div class="upload-description mb-2">
          เอกสารอื่น ๆ เช่น หนังสือมอบอำนาจ (ถ้ามี), สำเนาบัตรประชาชนผู้รับมอบอำนาจ (ถ้ามี)
        </div>
        <div
          v-if="!isEdit || documentSectionStatus.other !== TaxAgentStatus.Approved"
          class="upload-container mb-2"
        >
          <div class="upload-content-icon">
            <v-icon
              :color="otherHasError ? '#DC3F5A' : '#4CAF50'"
              size="24"
            >
              mdi-camera
            </v-icon>
          </div>
          <div class="upload-container-content">
            <div class="upload-area">
              <div class="upload-area-left">
                <div
                  v-if="documents.other.length > 0"
                  class="upload-content"
                >
                  <span
                    :class="
                      otherHasError
                        ? 'upload-content-header-error'
                        : 'upload-content-header'
                    "
                    color="#666666"
                  >เอกสารอื่น ๆ</span
                  >
                  <span class="primary--text _fs-16">
                    {{ documents.other.length }}/5 ไฟล์
                  </span>
                </div>
                <span
                  v-else
                  :class="
                    otherHasError
                      ? 'upload-counter-error'
                      : 'upload-counter'
                  "
                >
                  เอกสารอื่น ๆ (0/5)
                </span>
              </div>
              <div
                @click="
                  documents.other.length < MAX_FILES_PER_SECTION &&
                    triggerFileInput('otherInput')
                "
              >
                <v-icon
                  :class="
                    documents.other.length < MAX_FILES_PER_SECTION
                      ? 'upload-area-button'
                      : 'upload-area-button--disabled'
                  "
                  :color="otherHasError ? '#DC3F5A' : undefined"
                >
                  mdi-tray-arrow-up
                </v-icon>
              </div>
            </div>
            <input
              ref="otherInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              class="hidden-input"
              @change="handleFileChange($event, 'other')"
            />
            <div
              class="mt-1 pl-6"
              :class="uploadErrors.other || otherHasError ? 'upload-error' : 'upload-helper'"
            >
              กรุณาอัพโหลดเอกสารอื่น ๆ (สูงสุด 5 ไฟล์) รองรับไฟล์ jpg, png, pdf และมีขนาดไฟล์ไม่เกิน
              10mb
            </div>
            <div v-for="(item, index) in documents.other" :key="index" class="file-item mt-1">
              <v-icon size="20" color="#00BB03">mdi-file</v-icon>
              <div class="file-info">
                <span class="file-name">{{ item.name }}</span>
                <span class="file-size grey--text">{{ item.size }}</span>
              </div>
              <v-btn icon x-small @click="removeFile('other', index)">
                <v-icon size="18" color="#757575">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Submit button -->
    <div class="text-center mb-6 pt-6">
      <v-btn color="primary" rounded large min-width="180" @click="submit">
        ยืนยัน
      </v-btn>
    </div>

    <!-- Confirm Modal -->
    <v-dialog
      v-model="showConfirmModal"
      max-width="480"
      scrollable
      :persistent="submitting"
    >
      <v-card>
        <!-- Modal header -->
        <v-card-title class="d-flex justify-space-between align-center py-4 px-5">
          <span class="confirm-modal-title">ตรวจสอบและยืนยันการส่งข้อมูล</span>
          <v-btn icon small :disabled="submitting" @click="showConfirmModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="px-5 py-4 black--text">
          <!-- รายการที่แนบ -->
          <div class="mb-2">
            รายการที่แนบ
          </div>
          <div>
            <div v-if="companyDataChanged">
              <v-icon color="primary">mdi-circle-small</v-icon>
              <span>{{ SHOP_OR_COMPANY_LABEL }}</span>
            </div>
            <div v-if="isDocumentChanged(DOCUMENT_TYPE.COMPANY_CERTIFICATE)">
              <v-icon color="primary">mdi-circle-small</v-icon>
              <span>หนังสือรับรองบริษัท</span>
            </div>
            <div v-if="isDocumentChanged(DOCUMENT_TYPE.ID_CARD_COPY)">
              <v-icon color="primary">mdi-circle-small</v-icon>
              <span>สำเนาบัตรประจำตัวประชาชนผู้มอบอำนาจ</span>
            </div>
            <div v-if="isDocumentChanged(DOCUMENT_TYPE.TAX_AGENT_DOC)">
              <v-icon color="primary">mdi-circle-small</v-icon>
              <span>เอกสารการแต่งตั้ง บริษัท เซลสุกิ จำกัด</span>
            </div>
            <div v-if="isDocumentChanged(DOCUMENT_TYPE.OTHER_DOCS)">
              <v-icon color="primary">mdi-circle-small</v-icon>
              <span>เอกสารอื่น ๆ</span>
            </div>
          </div>
          <br>

          <!-- ข้อมูลร้านค้า หรือบริษัท -->
          <template v-if="companyDataChanged">
            <div class="mb-2">
              {{ SHOP_OR_COMPANY_LABEL }}
            </div>
            <div class="mb-1">
              <span class="font-weight-bold">Display Name:</span>
              <span class="primary--text ml-1">{{ activeLineOfficialAccount.displayName }}</span>
            </div>
            <div class="mb-1">
              <span class="font-weight-bold">Basic ID:</span>
              <span class="primary--text ml-1">{{ activeBasicId }}</span>
            </div>
            <div class="mb-1">
              ประเภทผู้เสียภาษี
              <span class="primary--text font-weight-bold ml-1">
                {{ $utils.customerTypeText(customerTypes, activeLineOfficialAccount.customerTypeId) }}
              </span>
            </div>
            <div v-if="isJuristicCustomer" class="mb-1">
              เลขที่สาขา
              <span class="black--text font-weight-bold ml-1">
                {{ activeLineOfficialAccount.branchNumber }}
                <template v-if="$utils.isHeadquarter(activeLineOfficialAccount.branchNumber)">
                  (สำนักงานใหญ่)
                </template>
              </span>
            </div>
            <div v-if="isJuristicCustomer" class="mb-1">
              {{ $utils.citizenIdText(isJuristicCustomer) }}
              <span class="black--text font-weight-bold ml-1">{{
                activeLineOfficialAccount.citizenId
              }}</span>
            </div>
            <div class="mb-3">
              ชื่อร้านค้า หรือบริษัท
              <span class="font-weight-bold ml-1">{{
                activeLineOfficialAccount.companyName
              }}</span>
            </div>

            <div class="mb-2">
              {{ SHOP_OR_COMPANY_LABEL }}
            </div>
            <v-row no-gutters class="mb-3">
              <v-col cols="6" class="mb-1">
                ชื่อ
                <span class="d-block font-weight-bold">{{
                  activeLineOfficialAccount.firstName
                }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                นามสกุล
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.lastName }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                Email สำหรับส่งใบกำกับภาษี
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.email }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                Email สำหรับส่งใบเสนอราคา
                <span class="d-block font-weight-bold">{{
                  activeLineOfficialAccount.emailQuotation
                }}</span>
              </v-col>
              <v-col cols="12">
                โทรศัพท์
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.tel }}</span>
              </v-col>
            </v-row>

            <!-- ที่อยู่ -->
            <div class="black--text mb-2">
              ที่อยู่ของร้านค้า หรือบริษัท
            </div>
            <v-row no-gutters class="black--text mb-3">
              <v-col cols="12" class="mb-1">
                ที่อยู่
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.address }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                ถนน
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.street }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                แขวง/ตำบล
                <span class="d-block font-weight-bold">{{
                  activeLineOfficialAccount.subdistrict
                }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                เขต/อำเภอ
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.district }}</span>
              </v-col>
              <v-col cols="6" class="mb-1">
                จังหวัด
                <span class="d-block font-weight-bold">{{
                  activeLineOfficialAccount.stateProvince
                }}</span>
              </v-col>
              <v-col cols="6">
                รหัสไปรษณีย์
                <span class="d-block font-weight-bold">{{ activeLineOfficialAccount.zipCode }}</span>
              </v-col>
            </v-row>
          </template>

          <!-- หมายเหตุ -->
          <div
            class="confirm-note black--text pa-4"
          >
            <span class="font-weight-bold">หมายเหตุ:</span>
            <div class="mt-1">
              บริษัทจะดำเนินการตรวจสอบความถูกต้องและแจ้งผลการอนุมัติให้ท่านทราบภายใน 3-5 วันทำการ
              ผ่านทางอีเมล หรือท่านสามารถติดตามสถานะได้ด้วยตนเองที่เมนู 'จัดการภาษี'
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Modal actions -->
        <v-card-actions class="pa-4 flex justify-center align-center">
          <v-btn
            color="primary"
            outlined
            rounded
            class="px-6 py-2 min-width-150"
            min-width="150"
            :disabled="submitting"
            @click="showConfirmModal = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="primary"
            rounded
            class="px-6 py-2 ml-4"
            min-width="150"
            :loading="submitting"
            :disabled="submitting"
            @click="confirmSubmit">
            ยืนยัน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import store from '@/store'
import countryCode from '@/utils/countryCode'
import { TaxAgentStatus } from '@/utils/defaultValue'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
const MAX_FILES_PER_SECTION = 5
const SHOP_OR_COMPANY_LABEL = 'ข้อมูลร้านค้า หรือบริษัท'
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf']

const DOCUMENT_TYPE = {
  COMPANY_CERTIFICATE: 'companyCertificate',
  ID_CARD_COPY: 'idCardCopy',
  TAX_AGENT_DOC: 'taxAgent',
  OTHER_DOCS: 'other'
}

const initialDocuments = () => Object.values(DOCUMENT_TYPE).reduce((acc, type) => {
  acc[type] = []
  return acc
}, {})

export default {
  name: 'TaxAgentCreateEdit',
  async beforeRouteEnter(to, from, next) {
    store.dispatch('wait/start', 'app loading', { root: true })
    if (to.meta.isEdit) {
      const citizenId = store.getters['User/activeLineOfficialAccount']?.citizenId
      if (citizenId) {
        await store.dispatch('TaxAgent/getTaxAgentByTaxId', citizenId)
      }
    }
    store.dispatch('wait/end', 'app loading', { root: true })
    next()
  },
  data() {
    return {
      DOCUMENT_TYPE,
      TaxAgentStatus,
      MAX_FILES_PER_SECTION,
      SHOP_OR_COMPANY_LABEL,
      showConfirmModal: false,
      submitting: false,
      emptyImageError: Object.values(DOCUMENT_TYPE).reduce((acc, type) => {
        acc[type] = false
        return acc
      }, {}),
      documents: initialDocuments(),
      existingDocuments: initialDocuments(),
      initialExistingCount: Object.values(DOCUMENT_TYPE).reduce((acc, type) => {
        acc[type] = 0
        return acc
      }, {}),
      uploadErrors: Object.values(DOCUMENT_TYPE).reduce((acc, type) => {
        acc[type] = false
        return acc
      }, {})
    }
  },
  created() {
    this.initiate()
  },
  computed: {
    ...mapState('User', ['userProfile']),
    ...mapState('CustomerType', ['customerTypes']),
    ...mapState('TaxAgent', ['taxAgent']),
    ...mapGetters({
      activeLineOfficialAccount: 'User/activeLineOfficialAccount',
      activeBasicId: 'User/activeBasicId'
    }),
    profile() {
      return this.activeLineOfficialAccount || {}
    },
    isJuristicCustomer() {
      return this.$utils.isJuristicCustomer(this.activeLineOfficialAccount.customerTypeId)
    },
    isEdit() {
      return this.$route.meta.isEdit
    },
    pageSubtitle() {
      return this.isEdit
        ? 'แก้ไขเอกสารการแต่งตั้งตัวแทนหักและนำส่งภาษี ณ ที่จ่าย'
        : 'ยื่นเอกสารการแต่งตั้งตัวแทนหักและนำส่งภาษี ณ ที่จ่าย'
    },
    taxAgentDocuments() {
      return this.taxAgent.tax_agent_document || this.taxAgent.taxAgentDocument || []
    },
    documentSectionStatus() {
      const status = {}
      Object.values(DOCUMENT_TYPE).forEach((type) => {
        const sectionDocs = this.taxAgentDocuments.filter((d) => d.document_type === type)
        if (sectionDocs.length === 0) {
          status[type] = null
        } else if (sectionDocs.some((d) => d.status === TaxAgentStatus.Rejected)) {
          status[type] = TaxAgentStatus.Rejected
        } else if (sectionDocs.every((d) => d.status === TaxAgentStatus.Approved)) {
          status[type] = TaxAgentStatus.Approved
        } else {
          status[type] = TaxAgentStatus.Pending
        }
      })
      return status
    },
    companyStatus() {
      return this.taxAgent?.company?.tax_agent_status || null
    },
    hasRejectedCompany() {
      return this.companyStatus === TaxAgentStatus.Rejected
    },
    otherHasError() {
      return (
        this.isEdit
        && this.documentSectionStatus.other === TaxAgentStatus.Rejected
        && this.documents.other.length === 0
      )
    },
    companyDataChanged() {
      if (!this.isEdit) return true
      if (this.companyStatus === TaxAgentStatus.Approved) return false
      const c = this.taxAgent && this.taxAgent.company
      if (!c) return true
      const a = this.activeLineOfficialAccount || {}
      const addr = c.address || {}
      const branch = c.branch || {}
      const norm = (v) => (v == null ? '' : String(v).trim())
      const combinedStreet = norm(`${a.address || ''} ${a.street || ''}`)
      return (
        norm(c.name) !== norm(a.companyName)
        || norm(c.tax_id) !== norm(a.citizenId)
        || norm(branch.number) !== norm(a.branchNumber)
        || norm(branch.email) !== norm(a.email)
        || norm(branch.phone) !== norm(a.tel)
        || norm(addr.administrative_area_level_1) !== norm(a.stateProvince)
        || norm(addr.administrative_area_level_2) !== norm(a.district)
        || norm(addr.locality_area_level_2) !== norm(a.subdistrict)
        || norm(addr.postal_code) !== norm(a.zipCode)
        || norm(addr.street) !== combinedStreet
      )
    }
  },
  methods: {
    ...mapActions({
      uploadPrivateDocument: 'File/uploadPrivateDocument',
      createTaxAgent: 'TaxAgent/createTaxAgent',
      updateTaxAgent: 'TaxAgent/updateTaxAgent',
      setTaxAgent: 'TaxAgent/setTaxAgent'
    }),
    async initiate() {
      if (!this.isEdit) return
      const grouped = initialDocuments()
      this.taxAgentDocuments.forEach((doc) => {
        const key = doc.document_type
        if (grouped[key] !== undefined && doc.status !== TaxAgentStatus.Rejected) {
          grouped[key].push(doc)
        }
      })
      this.existingDocuments = grouped
      Object.keys(grouped).forEach((key) => {
        this.initialExistingCount[key] = grouped[key].length
      })

      const requiredDocTypes = [
        DOCUMENT_TYPE.COMPANY_CERTIFICATE,
        DOCUMENT_TYPE.ID_CARD_COPY,
        DOCUMENT_TYPE.TAX_AGENT_DOC
      ]
      requiredDocTypes.forEach((type) => {
        if (this.existingDocuments[type].length === 0) {
          this.emptyImageError[type] = true
          this.uploadErrors[type] = true
        }
      })
    },
    isDocumentChanged(type) {
      if (!this.isEdit) {
        return type !== DOCUMENT_TYPE.OTHER_DOCS
      }
      const hasNew = this.documents[type].length > 0
      const removedExisting = this.existingDocuments[type].length < this.initialExistingCount[type]
      return hasNew || removedExisting
    },
    removeExistingFile(docKey, index) {
      this.existingDocuments[docKey].splice(index, 1)
    },
    triggerFileInput(refName) {
      this.$refs[refName].click()
    },
    handleFileChange(event, docKey) {
      const { target } = event
      const files = Array.from(target.files)
      const current = this.documents[docKey]
      const sumFiles = files.length + current.length

      this.uploadErrors[docKey] = ''
      this.emptyImageError[docKey] = false

      if (sumFiles > MAX_FILES_PER_SECTION) {
        this.$swal({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'กรุณาอัปโหลดไฟล์จำนวนไม่เกิน 5 ไฟล์'
        })
        this.uploadErrors[docKey] = true
        target.value = ''
        return
      }

      const validFiles = []
      let hasError = false

      files.slice(0, sumFiles).forEach((file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
          this.$swal({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'รองรับเฉพาะไฟล์ jpg, png และ pdf เท่านั้น'
          })
          this.uploadErrors[docKey] = true
          hasError = true
          return
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          this.$swal({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'กรุณาอัพโหลดไฟล์ที่มีขนาดไม่เกิน 10 MB'
          })
          this.uploadErrors[docKey] = true
          hasError = true
          return
        }
        validFiles.push({
          file,
          name: file.name,
          size: this.formatFileSize(file.size)
        })
      })

      if (!hasError && validFiles.length > 0) {
        this.uploadErrors[docKey] = ''
      }

      validFiles.reverse() // แก้ปัญหาไฟล์ใหม่ไปอยู่หลังไฟล์เก่า
      this.documents[docKey] = [...validFiles, ...current]
      target.value = ''
    },
    removeFile(docKey, index) {
      this.documents[docKey].splice(index, 1)
    },
    formatFileSize(bytes) {
      return `${Math.round(bytes / 1024)} KB`
    },
    submit() {
      const requiredDocs = [
        DOCUMENT_TYPE.COMPANY_CERTIFICATE,
        DOCUMENT_TYPE.ID_CARD_COPY,
        DOCUMENT_TYPE.TAX_AGENT_DOC
      ]

      requiredDocs.forEach((docKey) => {
        const hasNew = this.documents[docKey].length > 0
        const hasExisting = this.isEdit && this.existingDocuments[docKey].length > 0
        const isRejected = this.isEdit && this.documentSectionStatus[docKey] === TaxAgentStatus.Rejected
        this.emptyImageError[docKey] = isRejected ? !hasNew : (!hasNew && !hasExisting)
        if (this.emptyImageError[docKey]) {
          this.uploadErrors[docKey] = true
        }
      })

      if (this.isEdit) {
        const otherDocKey = DOCUMENT_TYPE.OTHER_DOCS
        if (this.documentSectionStatus[otherDocKey] === TaxAgentStatus.Rejected) {
          const hasNewOther = this.documents[otherDocKey].length > 0
          this.emptyImageError[otherDocKey] = !hasNewOther
          if (!hasNewOther) {
            this.uploadErrors[otherDocKey] = true
          }
        }
      }

      if (Object.values(this.emptyImageError).some((val) => val)) {
        this.showConfirmModal = false
        return
      }

      this.showConfirmModal = true
    },
    async confirmSubmit() {
      if (this.submitting) return
      this.submitting = true

      const uploadTasks = []
      const uploadMeta = []

      Object.entries(this.documents).forEach(([type, files]) => {
        files.forEach(({ file }) => {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('type', type)
          uploadTasks.push(this.uploadPrivateDocument(formData))
          uploadMeta.push(type)
        })
      })

      try {
        const res = await Promise.all(uploadTasks)

        const uploadCountByType = {}
        const taxDocuments = res.map((item, i) => {
          const docType = uploadMeta[i]
          const idx = uploadCountByType[docType] || 0
          uploadCountByType[docType] = idx + 1
          return {
            url: item.filePathURL,
            file_record_id: item.fileRecordId,
            document_type: docType,
            status: TaxAgentStatus.Pending,
            remark: ''
          }
        })

        if (this.isEdit) {
          Object.values(this.existingDocuments).forEach((docs) => {
            docs.forEach((doc) => {
              const status = doc.status === TaxAgentStatus.Approved
                ? TaxAgentStatus.Approved
                : TaxAgentStatus.Pending
              const files = Array.isArray(doc.files) ? doc.files : []
              if (files.length === 0) {
                return
              }
              files.forEach((file) => {
                taxDocuments.push({
                  ...(status === TaxAgentStatus.Approved ? { id: file.id } : {}),
                  url: file.url,
                  file_record_id: file.file_id,
                  document_type: doc.document_type,
                  status,
                  remark: doc.remark ?? ''
                })
              })
            })
          })
        }

        // using format of address data from Google Places API to match backend requirement in term of thailand address format
        const input = {
          company: {
            name: this.activeLineOfficialAccount.companyName || '',
            tax_id: this.activeLineOfficialAccount.citizenId || '',
            tax_agent_status: TaxAgentStatus.Pending,
            branch: {
              number: this.activeLineOfficialAccount.branchNumber || '',
              email: this.activeLineOfficialAccount.email || '',
              phone: this.activeLineOfficialAccount.tel || ''
            },
            address: {
              administrative_area_level_1: this.activeLineOfficialAccount.stateProvince || '',
              administrative_area_level_2: this.activeLineOfficialAccount.district || '',
              country_code: countryCode.THA,
              locality_area_level_1: this.activeLineOfficialAccount.stateProvince || '',
              locality_area_level_2: this.activeLineOfficialAccount.subdistrict || '',
              postal_code: this.activeLineOfficialAccount.zipCode || '',
              street: (`${this.activeLineOfficialAccount.address || ''} ${this.activeLineOfficialAccount.street || ''}`).trim()
            }
          },
          remark: '',
          status: TaxAgentStatus.Pending,
          tax_agent_document: taxDocuments
        }

        if (this.isEdit) {
          input.id = this.taxAgent?.id
          input.company.id = this.taxAgent?.company?.id
          input.company.branch.id = this.taxAgent?.company?.branch?.id
          input.company.address.id = this.taxAgent?.company?.branch?.address?.id
          input.company.tax_agent_status = this.taxAgent?.company?.tax_agent_status === TaxAgentStatus.Approved
            ? TaxAgentStatus.Approved
            : TaxAgentStatus.Pending

          await this.updateTaxAgent({ id: this.taxAgent?.company?.tax_id, data: input })
        } else {
          await this.createTaxAgent(input)
        }

        this.$router.push({ name: 'TaxAgent' })
      } catch (error) {
        this.$swal({
          icon: 'warning',
          title: 'เกิดข้อผิดพลาด',
          html: 'ขออภัย เกิดข้อผิดพลาดระหว่างการดำเนินการ<br>กรุณาลองใหม่อีกครั้ง'
        })
      } finally {
        this.submitting = false
        this.showConfirmModal = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  #tax-agent-create-page {
    padding: 0 120px;
    margin: 0;
  }

    .container {
    max-width: 100vw !important;
  }

  .user-info {
    padding: 40px 80px;
  }

  .tax-attachment {
    padding: 40px 80px;
  }

.page-title {
  font-size: 36px;
  color: #000;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 16px;
  color: #000;
  margin-bottom: 16px;
}

.section-header {
  font-size: 20px;
  color: #000;
}

.upload-section {
  .upload-label {
    font-size: 20px;
    color: #000;

    .required-mark {
      color: #DC3F5A;
    }
  }

  .upload-description {
    font-size: 16px;
    color: #757575;
  }

  .upload-area {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 32px;
    padding: 16px 24px;
    background-color: #f4f4f4;
    transition: background-color 0.2s;
    width: 100%;
    height: 56px;

    .upload-area-button {
      cursor: pointer;
      color: #4caf50;

      &.upload-area-button--disabled {
        cursor: not-allowed;
        color: #9ca3af;
      }
    }

    .upload-area-left {
      display: flex;
      align-items: center;
    }

    .upload-counter {
      font-size: 16px;
      color: #666666;
    }

    .upload-counter-error {
      font-size: 16px;
      color: #DC3F5A;
    }
  }

  .upload-content-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
 }

  .upload-content {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .upload-content-header {
      font-size: 10px;
      color: #666666;
    }

    .upload-content-header-error {
      font-size: 10px;
      color: #DC3F5A;
    }
  }

  .upload-helper {
    font-size: 14px;
    color: #9e9e9e;
  }

  .upload-error {
    font-size: 14px;
    color: #DC3F5A;
  }

  .hidden-input {
    display: none;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 16px;
    border-radius: 4px;
    padding: 8px;
    margin-top: 4px;
    border: 1px solid #E0E0E0;

    .file-name {
      font-size: 13px;
      color: #000;
    }

    .file-size {
      font-size: 12px;
      white-space: nowrap;
    }

    .file-info {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      flex-grow: 1;
    }
  }
}

.upload-container-content {
      width: 100%;
}

.upload-container {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 8px;
}

.confirm-note {
  background-color: #FFFBEB;
  border: 1px solid #D97706;
  border-radius: 4px;
}

.edit-error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #FDECEE;
  border: 1px solid #DC3F5A;
  border-radius: 4px;
  color: #DC3F5A;
  font-size: 16px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;

  &--approved {
    background-color: #E6F7EB;
    color: #00BB03;
  }

  &--rejected {
    background-color: #FDECEE;
    color: #DC3F5A;
  }
}

.footer-links {
  font-size: 13px;

  .footer-link {
    color: #757575;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .back-button {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 15px;
    color: #000;
    font-size: 16px;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
